import { createTheme, ThemeProvider } from '@mui/material';
import { jssPreset } from '@mui/styles';
import { create } from 'jss';
import { useEffect } from 'react';
import { createGenerateId, JssProvider } from 'react-jss';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Footer, Header } from './components';
import { Agreement, Contact, Home, Login, Logout, Privacy, Register } from './pages';
import { AuthService, getIdentity } from './services';

const styleNode = document.createComment('jss-insertion-point');
document.head.insertBefore(styleNode, document.head.firstChild);

const generateId = createGenerateId();
const jss = create({
	...jssPreset(),
	insertionPoint: 'jss-insertion-point',
});

const theme = createTheme({
	typography: {
		fontFamily: 'Noto Sans, sans-serif',
	},
});

const PrivateRoute = ({ element }: any) => {
	const isAuthenticated = AuthService.isAuth().val;
	return isAuthenticated ? element : <Navigate to="/login" />;
};

const AuthInitializer = () => {
	const navigate = useNavigate();

	useEffect(() => {
		let mounted = true;
		const init = async () => {
			const token = AuthService.bearerToken();
			if (!token) return;

			try {
				// Try to validate token by calling a lightweight identity endpoint.
				// Axios interceptors will attempt refresh if the token is expired.
				await getIdentity();
				// If successful, nothing to do (user stays authenticated).
			} catch (err) {
				// If identity call failed (refresh failed or 401), clear tokens and navigate to login.
				AuthService.logout();
				if (mounted) {
					navigate('/login', { replace: true });
				}
			}
		};

		init();
		return () => {
			mounted = false;
		};
	}, [navigate]);

	return null;
};

const Container = styled('div')`
  display: flex;
  margin: 0 auto;
  max-width: 900px;
  padding: 48px 24px;
  align-items: flex-start;
  justify-content: center;
  min-height: calc(100vh - 381px);
`;

export const App = () => {
	return (
		<JssProvider jss={jss} generateId={generateId}>
			<BrowserRouter>
				<ThemeProvider theme={theme}>
					<AuthInitializer />
					<Header />
					<Container>
						<Routes>
							<Route path={`/`} element={<PrivateRoute element={<Home />} />} />
							<Route path={`/login`} element={<Login />} />
							<Route path={`/logout`} element={<Logout />} />
							<Route path={`/privacy`} element={<Privacy />} />
							<Route path={`/register`} element={<Register />} />
							<Route path={`/agreement/:path`} element={<PrivateRoute element={<Agreement />} />} />
							<Route path={`/contact`} element={<Contact />} />
							<Route path={`/data-sharing-agreement`} element={<PrivateRoute element={<Home />} />} />
						</Routes>
					</Container>
					<Footer />
				</ThemeProvider>
			</BrowserRouter>
		</JssProvider>
	);
};
